<template>
  <div v-if="isFeatureFlagsVisible">
    <rest-form
      :disabled="isFeatureFlagsDisabled"
      ref="form"
      :model="value"
      :save-endpoint="saveEndpoint"
      :after-save="onSaved"
    >
      <form-section :title="sectionTitle">
        <template #controls>
          <save-button :click="onFormSaveButtonClicked" />
        </template>

        <div class="flags-grid">
          <div>
            <form-item
              prop="push_encryption"
              :label="$t('ProjectSettings_Field_Encryption')"
              :description="$t('ProjectSettings_Field_Encryption_Desc')"
            >
              <feature-toggle v-model="value.push_encryption" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="push_delivery_receipts"
              :label="$t('ProjectSettings_Field_PushDeliveryReceipts')"
              :description="
                $t('ProjectSettings_Field_PushDeliveryReceipts_Desc')
              "
            >
              <feature-toggle v-model="value.push_delivery_receipts" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="segmentation_events_use_profile"
              :label="$t('ProjectSettings_Field_SegmentationEventsUseProfile')"
              :description="
                $t('ProjectSettings_Field_SegmentationEventsUseProfile_Desc')
              "
            >
              <feature-toggle v-model="value.segmentation_events_use_profile" />
            </form-item>
          </div>
          <div>
            <div class="flexbox">
              <form-item
                prop="maxautomation"
                :label="$t('ProjectFeatures_Field_Maxautomation')"
                :description="
                  $t('ProjectFeatures_Field_Maxautomation_Description')
                "
              >
                <feature-toggle
                  v-model="value.maxautomation"
                  :disabled="mxaToggleDisabled"
                  @change="handleMaxautomationFlagChange"
                />
              </form-item>
              <form-item class="p-left-80" v-if="checkAuditLogLink">
                <el-link
                  :href="workflowActivityAuditLink"
                  target="_blank"
                  :underline="false"
                >
                  <el-button type="primary" plain icon="el-icon-notebook-2">
                    {{ $t('ProjectSettings_AuditLog') }}
                  </el-button>
                </el-link>
              </form-item>
            </div>
          </div>
          <template v-if="value.email">
            <div>
              <form-item
                prop="email_attachments"
                :label="$t('ProjectSettings_Field_EmailAttachments')"
                :description="$t('ProjectSettings_Field_EmailAttachments_Desc')"
              >
                <feature-toggle v-model="value.email_attachments" />
              </form-item>
            </div>
            <div>
              <form-item
                prop="profile_idfyby_email"
                :label="$t('ProjectSettings_Field_ProfileIdentifyByEmail')"
                :description="
                  $t('ProjectSettings_Field_ProfileIdentifyByEmail_Desc')
                "
              >
                <feature-toggle v-model="value.profile_idfyby_email" />
              </form-item>
            </div>
            <div>
              <form-item
                prop="profile_email_unique"
                :label="$t('ProjectSettings_Field_ProfileEmailUnique')"
                :description="
                  $t('ProjectSettings_Field_ProfileEmailUnique_Desc')
                "
              >
                <el-tooltip
                  effect="light"
                  :disabled="!value.gig_integration"
                  :content="$t('ProjectSettings_Tooltip_Profile_Email_Unique')"
                  placement="right"
                >
                  <feature-toggle
                    :disabled="!!value.gig_integration"
                    v-model="value.profile_email_unique"
                  />
                </el-tooltip>
              </form-item>
            </div>
          </template>
          <div v-if="$hasFeature('sportradarGlobalFlag')">
            <form-item
              prop="sportradar"
              :label="$t('ProjectSettings_Field_Sportradar')"
              :description="$t('ProjectSettings_Field_Sportradar_Desc')"
            >
              <feature-toggle v-model="value.sportradar" />
              <small class="col-12" v-if="shouldShowSportradarWarning">
                <br />
                <i class="el-icon-warning-outline"></i>
                {{ $t('ProjectSettings_Field_Sportradar_Warning') }}
              </small>
            </form-item>
          </div>
          <div>
            <form-item
              prop="content_import_export"
              :label="$t('ProjectSettings_Field_ContentImportExport')"
              :description="
                $t('ProjectSettings_Field_ContentImportExport_Desc')
              "
            >
              <feature-toggle v-model="value.content_import_export" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="subscription_tracking"
              :label="$t('ProjectSettings_Field_SubscriptionTracking')"
              :description="
                $t('ProjectSettings_Field_SubscriptionTracking_Desc')
              "
            >
              <feature-toggle v-model="value.subscription_tracking" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="subscription_preferences"
              :label="$t('ProjectSettings_Field_SubscriptionPreferences')"
              :description="
                $t('ProjectSettings_Field_SubscriptionPreferences_Desc')
              "
            >
              <el-tooltip
                effect="light"
                :disabled="!value.igt_integration"
                :content="
                  $t('ProjectSettings_Tooltip_Subscription_Preferences_IGT')
                "
                placement="right"
              >
                <feature-toggle
                  :disabled="!!value.igt_integration"
                  v-model="value.subscription_preferences"
                />
              </el-tooltip>
            </form-item>
          </div>
          <div>
            <form-item
              prop="meta"
              :label="$t('ProjectSettings_Field_Meta')"
              :description="$t('ProjectSettings_Field_Meta_Desc')"
            >
              <feature-toggle v-model="value.meta" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="googleads"
              :label="$t('ProjectSettings_Field_Googleads')"
              :description="$t('ProjectSettings_Field_Googleads_Desc')"
            >
              <feature-toggle v-model="value.googleads" />
            </form-item>
          </div>
          <div v-if="$hasFeature('micrositesGlobalFlag')">
            <form-item
              prop="microsites"
              :label="$t('ProjectSettings_Field_Microsites')"
              :description="$t('ProjectSettings_Field_Microsites_Desc')"
            >
              <feature-toggle v-model="value.microsites" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="campaign_engagement_delta_report"
              :label="$t('ProjectSettings_Field_CampaignEngagementDeltaReport')"
              :description="
                $t('ProjectSettings_Field_CampaignEngagementDeltaReport_Desc')
              "
            >
              <feature-toggle
                v-model="value.campaign_engagement_delta_report"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="gig_integration"
              :label="$t('ProjectSettings_Field_Gig_Integration')"
              :description="$t('ProjectSettings_Field_Gig_Integration_Desc')"
            >
              <el-tooltip
                effect="light"
                :disabled="!!value.profile_email_unique"
                :content="$t('ProjectSettings_Tooltip_GiG')"
                placement="right"
              >
                <feature-toggle
                  :disabled="!value.profile_email_unique"
                  v-model="value.gig_integration"
                />
              </el-tooltip>
            </form-item>
          </div>
          <div>
            <form-item
              prop="show_lithium_integration"
              :label="$t('ProjectSettings_Field_ShowLithiumIntegration')"
              :description="
                $t('ProjectSettings_Field_ShowLithiumIntegration_Desc')
              "
            >
              <feature-toggle v-model="value.show_lithium_integration" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="show_lithium_v2_integration"
              :label="$t('ProjectSettings_Field_ShowLithiumV2Integration')"
              :description="
                $t('ProjectSettings_Field_ShowLithiumV2Integration_Desc')
              "
            >
              <feature-toggle v-model="value.show_lithium_v2_integration" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="audit_log_profile"
              :label="$t('ProjectSettings_Field_AuditLog_Profile')"
              :description="$t('ProjectSettings_Field_AuditLog_Profile_Desc')"
            >
              <feature-toggle v-model="value.audit_log_profile" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="audit_log_campaign"
              :label="$t('ProjectSettings_Field_AuditLog_Campaign')"
              :description="$t('ProjectSettings_Field_AuditLog_Campaign_Desc')"
            >
              <feature-toggle v-model="value.audit_log_campaign" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="feature_seed_list"
              :label="$t('ProjectSettings_Field_Feature_SeedList')"
              :description="$t('ProjectSettings_Field_Feature_SeedList_Desc')"
            >
              <feature-toggle v-model="value.feature_seed_list" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="disable_event_triggered_campaign_timeseries_segment"
              :label="
                $t('ProjectSettings_Field_Event_Triggered_Timeseries_Segments')
              "
              :description="
                $t(
                  'ProjectSettings_Field_Event_Triggered_Timeseries_Segments_Desc'
                )
              "
            >
              <feature-toggle
                v-model="
                  value.disable_event_triggered_campaign_timeseries_segment
                "
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="disable_mobile_validation"
              :label="$t('ProjectSettings_Field_Disable_Mobile_Validation')"
              :description="
                $t('ProjectSettings_Field_Disable_Mobile_Validation_Desc')
              "
            >
              <feature-toggle v-model="value.disable_mobile_validation" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="inapp_native"
              :label="$t('ProjectSettings_Field_Feature_InappNative')"
              :description="
                $t('ProjectSettings_Field_Feature_InappNative_Desc')
              "
            >
              <feature-toggle v-model="value.inapp_native" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="ai_text_generation"
              :label="$t('ProjectSettings_Field_Ai')"
              :description="$t('ProjectSettings_Field_Ai_Desc')"
            >
              <feature-toggle v-model="value.ai_text_generation" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="campaign_templates"
              :label="$t('ProjectSettings_Field_CampaignTemplates')"
              :description="$t('ProjectSettings_Field_CampaignTemplates_Desc')"
            >
              <feature-toggle v-model="value.campaign_templates" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="betsson_integration"
              :label="$t('ProjectSettings_Field_Betsson_Integration')"
              :description="
                $t('ProjectSettings_Field_Betsson_Integration_Desc')
              "
            >
              <feature-toggle v-model="value.betsson_integration" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="feature_campaign_throttling"
              :label="$t('ProjectSettings_Field_Feature_CampaignThrottling')"
              :description="
                $t('ProjectSettings_Field_Feature_CampaignThrottling_Desc')
              "
            >
              <el-select
                v-model="value.feature_campaign_throttling"
                data-qa="feature_campaign_throttling"
              >
                <el-option
                  v-for="(label, mode) in campaignThrottlingModeLabels"
                  :key="mode"
                  :value="parseInt(mode)"
                  :label="$t(label)"
                />
              </el-select>
            </form-item>
          </div>
          <div>
            <form-item
              prop="allow_broadcast_for_multistage_timebased_campaigns"
              :label="
                $t(
                  'ProjectSettings_Field_Feature_AllowBroadcastForMultistageTimebasedCampaigns'
                )
              "
              :description="
                $t(
                  'ProjectSettings_Field_Feature_AllowBroadcastForMultistageTimebasedCampaigns'
                )
              "
            >
              <feature-toggle
                v-model="
                  value.allow_broadcast_for_multistage_timebased_campaigns
                "
                :active-text="$t('ProjectSettings_Toggle_Allowed')"
                :inactive-text="$t('ProjectSettings_Toggle_Disallowed')"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="show_open_bet_integration"
              :label="$t('ProjectSettings_Field_ShowOpenBetIntegration')"
              :description="
                $t('ProjectSettings_Field_ShowOpenBetIntegration_Desc')
              "
            >
              <feature-toggle v-model="value.show_open_bet_integration" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="automation_campaign_execution"
              :label="
                $t('ProjectSettings_Field_Profile_Import_Campaign_Trigger')
              "
              :description="
                $t('ProjectSettings_Field_Profile_Import_Campaign_Trigger_Desc')
              "
            >
              <feature-toggle v-model="value.automation_campaign_execution" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="show_inbox_open_stats"
              :label="$t('ProjectSettings_Field_ShowInboxOpenStats')"
              :description="$t('ProjectSettings_Field_ShowInboxOpenStats_Desc')"
            >
              <feature-toggle v-model="value.show_inbox_open_stats" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="igt_integration"
              :label="$t('ProjectSettings_Field_Igt_Integration')"
              :description="$t('ProjectSettings_Field_Igt_Integration_Desc')"
            >
              <el-tooltip
                effect="light"
                :disabled="!!value.subscription_preferences"
                :content="$t('ProjectSettings_Tooltip_IGT_Requirements')"
                placement="right"
              >
                <feature-toggle
                  :disabled="!value.subscription_preferences"
                  v-model="value.igt_integration"
                />
              </el-tooltip>
            </form-item>
          </div>
          <div>
            <form-item
              prop="opap_webhook_integration"
              :label="$t('ProjectSettings_Field_OPAPWebhookIntegration')"
              :description="
                $t('ProjectSettings_Field_OPAPWebhookIntegration_Desc')
              "
            >
              <feature-toggle v-model="value.opap_webhook_integration" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="allow_authorised_devices"
              :label="$t('ProjectSettings_Field_AllowAuthorisedDevices')"
              :description="
                $t('ProjectSettings_Field_AllowAuthorisedDevices_Desc')
              "
            >
              <feature-toggle v-model="value.allow_authorised_devices" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="workflow_clickmap"
              :label="$t('ProjectSettings_Field_WorkflowClickmap')"
              :description="$t('ProjectSettings_Field_WorkflowClickmap_Desc')"
            >
              <feature-toggle v-model="value.workflow_clickmap" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="save_as_report_campaign_start_time"
              :label="$t('ProjectSettings_Field_SaveAsReportCampaignStartTime')"
              :description="
                $t('ProjectSettings_Field_SaveAsReportCampaignStartTime_Desc')
              "
            >
              <feature-toggle
                v-model="value.save_as_report_campaign_start_time"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="save_as_report_delta"
              :label="$t('ProjectSettings_Field_SaveAsReportDelta')"
              :description="$t('ProjectSettings_Field_SaveAsReportDelta_Desc')"
            >
              <feature-toggle v-model="value.save_as_report_delta" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="allow_voucher_codes"
              :label="$t('ProjectSettings_Field_VoucherCode')"
              :description="$t('ProjectSettings_Field_VoucherCode_Desc')"
            >
              <feature-toggle v-model="value.allow_voucher_codes" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="oasis_integration"
              :label="$t('ProjectSettings_Field_Oasis_Integration')"
              :description="$t('ProjectSettings_Field_Oasis_Integration_Desc')"
            >
              <feature-toggle v-model="value.oasis_integration" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="campaign_content_preview"
              :label="$t('ProjectSettings_Field_Campaign_Content_Preview')"
              :description="
                $t('ProjectSettings_Field_Campaign_Content_Preview_Desc')
              "
            >
              <feature-toggle v-model="value.campaign_content_preview" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="advanced_profile_search"
              :label="$t('ProjectSettings_Field_Advanced_Profile_Search')"
              :description="
                $t('ProjectSettings_Field_Advanced_Profile_Search_Desc')
              "
            >
              <feature-toggle v-model="value.advanced_profile_search" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="optimove_lists"
              :label="$t('ProjectSettings_Field_Optimove_Lists')"
              :description="$t('ProjectSettings_Field_Optimove_Lists_Desc')"
            >
              <feature-toggle v-model="value.optimove_lists" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="action_aggregation_enabled"
              :label="$t('ProjectSettings_Field_Action_Message_Aggregation')"
              :description="
                $t('ProjectSettings_Field_Action_Message_Aggregation_Desc')
              "
            >
              <feature-toggle v-model="value.action_aggregation_enabled" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="no_changed_sub_tracking"
              :label="$t('ProjectSettings_Field_NoChanged_Subs')"
              :description="$t('ProjectSettings_Field_NoChanged_Subs_Desc')"
            >
              <feature-toggle v-model="value.no_changed_sub_tracking" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="no_changed_attribute_handling"
              :label="$t('ProjectSettings_Field_NoChanged_Attribute_Handling')"
              :description="
                $t('ProjectSettings_Field_NoChanged_Attribute_Handling_Desc')
              "
            >
              <feature-toggle v-model="value.no_changed_attribute_handling" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="gateway_cost_tracking"
              :label="$t('ProjectSettings_Field_Feature_GatewayCostTracking')"
              :description="
                $t('ProjectSettings_Field_Feature_GatewayCostTracking_Desc')
              "
            >
              <feature-toggle v-model="value.gateway_cost_tracking" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="enable_new_goal_conversion"
              :label="$t('ProjectSettings_Field_EnableNewGoalConversion')"
              :description="
                $t('ProjectSettings_Field_EnableNewGoalConversion_Desc')
              "
            >
              <feature-toggle v-model="value.enable_new_goal_conversion" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="enable_campaign_comparison_conversions"
              :label="
                $t('ProjectSettings_Field_EnableCampaignComparisonConversions')
              "
              :description="
                $t(
                  'ProjectSettings_Field_EnableCampaignComparisonConversions_Desc'
                )
              "
            >
              <feature-toggle
                v-model="value.enable_campaign_comparison_conversions"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="queue_email_msg_id_only"
              :label="$t('ProjectSettings_Field_QueueEmailMsgIdOnly')"
              :description="
                $t('ProjectSettings_Field_QueueEmailMsgIdOnly_Desc')
              "
            >
              <feature-toggle v-model="value.queue_email_msg_id_only" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="enable_email_last_open"
              :label="$t('ProjectSettings_Field_SegmentEmailLastOpened')"
              :description="
                $t('ProjectSettings_Field_SegmentEmailLastOpened_Desc')
              "
            >
              <feature-toggle v-model="value.enable_email_last_open" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="preaggregated_clicked_links"
              :label="
                $t('ProjectSettings_Field_Feature_PreaggregatedClickedLinks')
              "
              :description="
                $t(
                  'ProjectSettings_Field_Feature_PreaggregatedClickedLinks_Desc'
                )
              "
            >
              <feature-toggle v-model="value.preaggregated_clicked_links" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="mxa_campaign_options"
              :label="$t('ProjectSettings_Field_Feature_MxaCampaignOptions')"
              :description="
                $t('ProjectSettings_Field_Feature_MxaCampaignOptions_Desc')
              "
            >
              <feature-toggle v-model="value.mxa_campaign_options" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="campaign_list_pagination_disable"
              :label="
                $t(
                  'ProjectSettings_Field_Feature_CampaignListPaginationDisable'
                )
              "
              :description="
                $t(
                  'ProjectSettings_Field_Feature_CampaignListPaginationDisable_Desc'
                )
              "
            >
              <feature-toggle
                v-model="value.campaign_list_pagination_disable"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="allow_five_character_language_code"
              :label="
                $t(
                  'ProjectSettings_Field_Feature_AllowFiveCharacterLanguageCode'
                )
              "
              :description="
                $t(
                  'ProjectSettings_Field_Feature_AllowFiveCharacterLanguageCode_Desc'
                )
              "
            >
              <feature-toggle
                v-model="value.allow_five_character_language_code"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="ios_live_activities"
              :label="$t('ProjectSettings_Field_Ios_Live_Activities')"
              :description="
                $t('ProjectSettings_Field_Ios_Live_Activities_Desc')
              "
            >
              <feature-toggle v-model="value.ios_live_activities" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="new_mxa"
              :label="$t('ProjectSettings_Field_New_Mxa')"
              :description="$t('ProjectSettings_Field_New_Mxa_Desc')"
            >
              <feature-toggle
                :disabled="isJbTwoFlagDisabled"
                v-model="value.new_mxa"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="campaign_trigger_by_consent_pref_events"
              :label="
                $t('ProjectSettings_Field_CampaignTriggerByConsentPrefEvents')
              "
              :description="
                $t(
                  'ProjectSettings_Field_CampaignTriggerByConsentPrefEvents_Desc'
                )
              "
            >
              <feature-toggle
                v-model="value.campaign_trigger_by_consent_pref_events"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="rum_tracking"
              :label="$t('ProjectSettings_Field_Rum_Tracking')"
              :description="$t('ProjectSettings_Field_Rum_Tracking_Desc')"
            >
              <feature-toggle v-model="value.rum_tracking" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="mxa_migration_tool"
              :label="$t('ProjectSettings_Field_Mxa_Migration_Tool')"
              :description="$t('ProjectSettings_Field_Mxa_Migration_Tool_Desc')"
            >
              <feature-toggle
                v-model="value.mxa_migration_tool"
                :disabled="!value.new_mxa"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="mxa_event_node"
              :label="$t('ProjectSettings_Field_Mxa_Event_Node')"
              :description="$t('ProjectSettings_Field_Mxa_Event_Node_Desc')"
            >
              <feature-toggle
                v-model="value.mxa_event_node"
                :disabled="!value.new_mxa"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="accumulation"
              :label="$t('ProjectSettings_Field_Accumulation_Triggers')"
              :description="
                $t('ProjectSettings_Field_Accumulation_Triggers_Desc')
              "
            >
              <feature-toggle v-model="value.accumulation" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="accumulation_interval"
              :label="
                $t('ProjectSettings_Field_Accumulation_Triggers_Interval')
              "
              :description="
                $t('ProjectSettings_Field_Accumulation_Triggers_Interval_Desc')
              "
              :rules="accumulationRules"
            >
              <el-input-number
                v-model="value.accumulation_interval"
                :min="0"
                :step="1"
                :precision="0"
                controls-position="right"
                :disabled="!value.accumulation"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="early_cancel_deduplication_window"
              :label="
                $t('ProjectSettings_Field_Early_Cancel_Deduplication_Window')
              "
              :description="
                $t(
                  'ProjectSettings_Field_Early_Cancel_Deduplication_Window_Desc'
                )
              "
            >
              <el-input-number
                v-model="value.early_cancel_deduplication_window"
                data-qa="early-cancel-deduplication-window"
                :min="0"
                :step="1"
                :precision="0"
                controls-position="right"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="early_cancel_lookup_window"
              :label="$t('ProjectSettings_Field_Early_Cancel_Lookup_Window')"
              :description="
                $t('ProjectSettings_Field_Early_Cancel_Lookup_Window_Desc')
              "
            >
              <el-input-number
                v-model="value.early_cancel_lookup_window"
                data-qa="early-cancel-lookup-window"
                :min="0"
                :step="1"
                :precision="0"
                controls-position="right"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="warply_integration"
              :label="$t('ProjectSettings_Field_ShowWarplyIntegration')"
              :description="
                $t('ProjectSettings_Field_ShowWarplyIntegration_Desc')
              "
            >
              <feature-toggle v-model="value.warply_integration" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="open_search_enabled"
              :label="$t('ProjectSettings_Field_OpenSearch_Integration')"
              :description="
                $t('ProjectSettings_Field_OpenSearch_Integration_Desc')
              "
            >
              <feature-toggle v-model="value.open_search_enabled" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="webhook_stream_integration"
              :label="$t('Webhook_Stream_Integration_Label')"
              :description="
                $t('ProjectSettings_Field_Webhook_Stream_Integration_Desc')
              "
            >
              <el-tooltip
                :content="
                  $t('ProjectSettings_Field_Webhook_Stream_Integration_ToolTip')
                "
                placement="right"
                effect="light"
                :disabled="!value.living_segments"
              >
                <feature-toggle
                  v-model="value.webhook_stream_integration"
                  :disabled="!!value.living_segments"
                  data-qa="webhook-stream-integration-feature-flag"
                />
              </el-tooltip>
            </form-item>
          </div>
          <div>
            <form-item
              prop="living_segments"
              :label="$t('Living_Segments_Label')"
              :description="$t('ProjectSettings_Field_Living_Segments_Desc')"
            >
              <el-tooltip
                :content="$t('ProjectSettings_Field_Living_Segments_ToolTip')"
                placement="right"
                effect="light"
                :disabled="!!value.webhook_stream_integration"
              >
                <feature-toggle
                  v-model="value.living_segments"
                  :disabled="!value.webhook_stream_integration"
                  data-qa="living-segments-feature-flag"
                />
              </el-tooltip>
            </form-item>
          </div>
          <div v-if="$hasReleaseFlag('external-segment-picker')">
            <form-item
              prop="external_segment_picker"
              :label="$t('ProjectFeatures_Field_ExternalSegmentPicker')"
              :description="
                $t('ProjectFeatures_Field_ExternalSegmentPicker_Description')
              "
            >
              <feature-toggle
                v-model="value.external_segment_picker"
                data-qa="external-segment-picker-feature-flag"
              />
            </form-item>
          </div>
          <div v-if="$hasReleaseFlag('sms-usage-reporting')">
            <form-item
              prop="sms_usage_reporting"
              :label="$t('ProjectFeatures_Field_SmsUsageReporting')"
              :description="
                $t('ProjectFeatures_Field_SmsUsageReporting_Description')
              "
            >
              <feature-toggle
                v-model="value.sms_usage_reporting"
                data-qa="sms-usage-reporting-feature-flag"
              />
            </form-item>
          </div>
          <div v-if="$hasReleaseFlag('kafka-sport-feed')">
            <form-item
              prop="kafka_sport_feed_enabled"
              :label="$t('ProjectFeatures_Field_KafkaSportFeed')"
              :description="
                $t('ProjectFeatures_Field_KafkaSportFeed_Description')
              "
            >
              <feature-toggle
                v-model="value.kafka_sport_feed_enabled"
                data-qa="kafka-sport-feed-enabled-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="auto_generate_email"
              :label="$t('ProjectSettings_Field_AutoGenerateEmail')"
              :description="$t('ProjectSettings_Field_AutoGenerateEmail_Desc')"
            >
              <feature-toggle
                v-model="value.auto_generate_email"
                data-qa="auto-generate-email-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="auto_generate_mobile"
              :label="$t('ProjectSettings_Field_AutoGenerateMobile')"
              :description="$t('ProjectSettings_Field_AutoGenerateMobile_Desc')"
            >
              <feature-toggle
                v-model="value.auto_generate_mobile"
                data-qa="auto-generate-mobile-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="profile_importer_type"
              :label="$t('ProjectSettings_Field_Batch_Profile_Import')"
              :description="
                $t('ProjectSettings_Field_Batch_Profile_Import_Desc')
              "
            >
              <feature-toggle v-model="batchImporterInverted" />
            </form-item>
          </div>
          <div>
            <form-item
              prop="disable_kafka_stream_event_filtering"
              :label="
                $t('ProjectSettings_Field_Disable_Kafka_Stream_Event_Filtering')
              "
              :description="
                $t(
                  'ProjectSettings_Field_Disable_Kafka_Stream_Event_Filtering_Desc'
                )
              "
            >
              <feature-toggle
                v-model="value.disable_kafka_stream_event_filtering"
                data-qa="disable-kafka-stream-event-filtering-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="tag_categories"
              :label="$t('ProjectSettings_Field_TagCategories')"
              :description="$t('ProjectSettings_Field_TagCategories_Desc')"
            >
              <feature-toggle
                v-model="value.tag_categories"
                data-qa="tag-categories-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="content_fetch"
              :label="$t('ProjectSettings_Field_Content_Fetch')"
              :description="$t('ProjectSettings_Field_Content_Fetch_Desc')"
            >
              <feature-toggle
                v-model="value.content_fetch"
                data-qa="content-fetch-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="content_fetch_retry"
              :label="$t('ProjectSettings_Field_Content_Fetch_Retry')"
              :description="
                $t('ProjectSettings_Field_Content_Fetch_Retry_Desc')
              "
            >
              <feature-toggle
                v-model="value.content_fetch_retry"
                data-qa="content-fetch-retry-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="stricter_tag_validation"
              :label="$t('ProjectSettings_Field_StricterTagValidation')"
              :description="
                $t('ProjectSettings_Field_StricterTagValidation_Desc')
              "
            >
              <feature-toggle
                v-model="value.stricter_tag_validation"
                data-qa="stricter-tag-validation-feature-flag"
              />
            </form-item>
          </div>

          <div>
            <form-item
              prop="customer_id_enabled"
              :label="$t('ProjectSettings_Field_Customer_Id_Enabled')"
              :description="
                $t('ProjectSettings_Field_Customer_Id_Enabled_Desc')
              "
            >
              <feature-toggle
                v-model="value.customer_id_enabled"
                data-qa="customer-id-enabled-feature-flag"
              />
            </form-item>
          </div>

          <div>
            <form-item
              prop="legacy_mxa_enabled"
              :label="$t('Campaign_Dialog_OldMxa')"
              :description="$t('Campaign_Dialog_OldMxa_Description')"
            >
              <el-tooltip
                :disabled="!isJbOneFlagDisabled"
                :content="$t('CampaignTooltip_OldMxa_Disable_Message')"
                effect="light"
                placement="right"
              >
                <feature-toggle
                  v-model="value.legacy_mxa_enabled"
                  :disabled="isJbOneFlagDisabled"
                />
              </el-tooltip>
            </form-item>
          </div>
          <div>
            <form-item
              prop="comeon_integration"
              :label="$t('Campaign_Dialog_ComeOn')"
              :description="$t('Campaign_Dialog_ComeOn_Description')"
            >
              <feature-toggle
                v-model="value.comeon_integration"
                data-qa="comeon-integration-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="open_live_activities"
              :label="$t('ProjectSettings_Field_Open_Live_Activities')"
              :description="
                $t('ProjectSettings_Field_Open_Live_Activities_Desc')
              "
            >
              <feature-toggle
                v-model="value.open_live_activities"
                data-qa="open-live-activities-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="open_live_activities_batch_size"
              :label="
                $t('ProjectSettings_Field_Open_Live_Activities_Batch_Size')
              "
              :description="
                $t('ProjectSettings_Field_Open_Live_Activities_Batch_Size_Desc')
              "
            >
              <el-input-number
                v-model="value.open_live_activities_batch_size"
                :min="0"
                :max="10000"
                data-qa="open-live-activities-batch-size-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="legacy_mxa_enabled"
              :label="$t('Campaign_Dialog_OldMxa')"
              :description="$t('Campaign_Dialog_OldMxa_Description')"
            >
              <el-tooltip
                :disabled="!isJbOneFlagDisabled"
                :content="$t('CampaignTooltip_OldMxa_Disable_Message')"
                effect="light"
                placement="right"
              >
                <feature-toggle
                  v-model="value.legacy_mxa_enabled"
                  :disabled="isJbOneFlagDisabled"
                />
              </el-tooltip>
            </form-item>
          </div>
          <div>
            <form-item
              prop="tag_operator_validation"
              :label="$t('ProjectSettings_Field_TagOperatorValidation')"
              :description="
                $t('ProjectSettings_Field_TagOperatorValidation_Desc')
              "
            >
              <feature-toggle
                v-model="value.tag_operator_validation"
                data-qa="tag-operator-validation-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="apptoken_enabled"
              :label="$t('ProjectSettings_Field_ApptokenEnabled')"
              :description="$t('ProjectSettings_Field_ApptokenEnabled_Desc')"
            >
              <feature-toggle
                v-model="value.apptoken_enabled"
                data-qa="appkey-enabled-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="profile_import_handle_id_conflicts"
              :label="
                $t('ProjectSettings_Field_ProfileImportHandleIdConflicts')
              "
              :description="
                $t('ProjectSettings_Field_ProfileImportHandleIdConflicts_Desc')
              "
            >
              <feature-toggle
                v-model="value.profile_import_handle_id_conflicts"
                data-qa="profile-import-handle-id-conflicts-feature-flag"
              />
            </form-item>
          </div>
          <div>
            <form-item
              prop="profile_require_user_id"
              :label="$t('ProjectSettings_Field_ProfileRequireUserId')"
              :description="
                $t('ProjectSettings_Field_ProfileRequireUserId_Desc')
              "
            >
              <feature-toggle
                v-model="value.profile_require_user_id"
                data-qa="profile-require-user-id-feature-flag"
              />
            </form-item>
          </div>
        </div>
      </form-section>
    </rest-form>
    <save-features-dialog
      :features="changedFeatures"
      @close="showSaveDialog = false"
      v-if="showSaveDialog"
      :save="onSaveDialogSaveClicked"
    />
  </div>
</template>

<script>
import FormItem from '@/common/components/form/FormItem'
import { $isSuperAdmin } from '@/core/utils/VueAcl'
import { mapGetters } from 'vuex'
import FeatureToggle from '@/common/components/form/FeatureToggle'
import SaveButton from '@/common/components/form/SaveButton.vue'
import RestForm from '@/common/components/rest/RestForm.vue'
import FormSection from '@/common/components/form/FormSection.vue'
import { projectEndpoint } from '@/core/helpers'
import SaveFeaturesDialog from '@/modules/project/components/SaveFeaturesDialog.vue'
import { projectFlagState } from '@/modules/project/enum/ProjectFlagStateEnum'
import { campaignThrottlingModeLabels } from '@/modules/project/enum/CampaignThrottlingModeEnum'

export default {
  name: 'XpFeatureFlags',
  components: {
    SaveFeaturesDialog,
    FormSection,
    RestForm,
    SaveButton,
    FeatureToggle,
    FormItem
  },

  props: {
    /** A project model object */
    value: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      // this is used to compare edited values against the original values
      originalValue: { ...this.value },
      invertedProperties: ['profile_importer_type'],
      showSaveDialog: false,
      campaignThrottlingModeLabels
    }
  },

  computed: {
    ...mapGetters(['mxaDomain']),

    sectionTitle() {
      return this.$t('ProjectFeatures_XP_Features')
    },

    saveEndpoint() {
      return projectEndpoint({
        method: 'PATCH',
        url: ''
      })
    },

    changedFeatures() {
      let changed = []

      for (const key of Object.keys(this.originalValue)) {
        if (this.originalValue[key] !== this.value[key]) {
          changed.push({
            name: key,
            status: this.value[key],
            inverted: this.invertedProperties.includes(key)
          })
        }
      }

      return changed
    },

    workflowActivityAuditLink() {
      return `https://${this.mxaDomain}/project/${this.value.id}/admin/log`
    },
    checkAuditLogLink() {
      return !!this.value.maxautomation && $isSuperAdmin()
    },
    shouldShowSportradarWarning() {
      return this.changedFeatures.find(
        feature => feature.name === 'sportradar' && feature.status === 0
      )
    },
    accumulationRules() {
      let rule = {
        type: 'number',
        required: true,
        message: 'Please enter an integer',
        trigger: 'blur'
      }
      return this.value.accumulation ? [rule] : []
    },
    isFeatureFlagsVisible() {
      return this.$can('ProjectFeatureFlags::Read')
    },
    isFeatureFlagsDisabled() {
      return !this.$can('ProjectFeatureFlags::Manage')
    },
    mxaToggleDisabled() {
      return !this.value.maxautomation && this.value.maxautomation_disabled
    },
    batchImporterInverted: {
      get() {
        return this.value.profile_importer_type === 0 ? 1 : 0
      },
      set(value) {
        this.value.profile_importer_type = value === 1 ? 0 : 1
      }
    },
    isJbOneFlagDisabled() {
      // prevent turning JB1 off when maxautomation is on and JB2 is already off
      return !!(
        this.value.maxautomation &&
        this.value.legacy_mxa_enabled &&
        !this.value.new_mxa
      )
    },
    isJbTwoFlagDisabled() {
      return !!this.value.new_mxa
    }
  },

  methods: {
    onFormSaveButtonClicked() {
      if (this.changedFeatures.length > 0) {
        this.showSaveDialog = true
      } else {
        this.saveFeatureFlags()
      }
    },
    onSaveDialogSaveClicked() {
      this.saveFeatureFlags()
      this.showSaveDialog = false
    },
    onSaved() {
      this.$emit('save')
    },
    saveFeatureFlags() {
      this.$refs['form'].save()
    },
    handleMaxautomationFlagChange(event) {
      if (
        event === projectFlagState.ENABLED &&
        this.value.new_mxa === projectFlagState.DISABLED
      ) {
        this.value.new_mxa = projectFlagState.ENABLED
      }
    }
  },

  watch: {
    'value.new_mxa'(value) {
      if (!value) {
        this.value.mxa_migration_tool = 0
      }
    }
  }
}
</script>

<style scoped>
.flexbox {
  display: flex;
  align-items: flex-end;
}

small {
  color: #7e8596;
}

.flags-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 15px;
}
</style>



// WEBPACK FOOTER //
// src/modules/project/components/XpFeatureFlags.vue